import BuildShowStore from "~/stores/BuildShowStore";
import { Build, Job, CommandJob } from "~/components/build/Show/lib/types";
import {
  startTransition,
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { dropRight, filter, isNumber, matchesProperty, noop } from "lodash";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { stepDependenciesQueryOptions } from "~/components/build/Show/lib/queries/stepDependencies";
import { useStepDependenciesStore } from "~/components/build/Show/lib/useStepDependenciesStore";
import { QueryObserver, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  allStepsQueryKey,
  stepsByStateQueryKey,
  topLevelStepsQueryKey,
} from "~/components/build/Show/lib/queries/steps";
import { Step } from "~/lib/pipeline";
import { StateOrder } from "~/components/build/Show/components/Sidebar/useFilterStore";
import { useStepsQuery } from "~/components/build/Show/lib/useSteps";
import { stepQueryKey } from "~/components/build/Show/lib/queries/step";

const BuildContext = createContext<NullableBuildContext>({
  build: null,
  store: null,
  setBuild: noop,
});

export const BuildContextProvider = ({
  children,
  store,
}: PropsWithChildren<{ store: BuildShowStore }>) => {
  const [build, setBuild] = useState(store.build);
  const setSteps = useBuildStore((state) => state.setSteps);
  const setJobs = useBuildStore((state) => state.setJobs);
  const queryClient = useQueryClient();

  /**
   * Fetch the step dependency graph, and store it in the useStepDependenciesStore.
   */
  const stepDependenciesQuery = useQuery(
    stepDependenciesQueryOptions(build, build.stepUploadsCount),
  );
  useEffect(() => {
    if (stepDependenciesQuery.data && Object.keys(stepDependenciesQuery.data).length > 0) {
      useStepDependenciesStore.getState().setDependenciesByStep(stepDependenciesQuery.data);
    }
  }, [stepDependenciesQuery.data]);

  useEffect(() => {
    if (!Features.ProgressiveBuildShow) {
      return;
    }

    // Keep track of the last updated data for each query to unnecessary updates to the store.
    const queriesDataUpdatedAt = new Map();

    // Watch top-level steps related queries and jam em' into the store.
    const stepQueryKeys = [
      allStepsQueryKey(build),
      topLevelStepsQueryKey(build),
      ...StateOrder.map((state) => stepsByStateQueryKey(build, state)),
    ];

    const unsubscribes = stepQueryKeys.map((queryKey) => {
      const observer = new QueryObserver(queryClient, {
        queryKey,
        // Don't execute the query, just listen for changes
        enabled: false,
      });

      return observer.subscribe((result) => {
        const prevDataUpdatedAt = queriesDataUpdatedAt.get(queryKey) || 0;

        // Check if the data has been updated since the last time we received it before
        // updating the store and triggering re-renders.
        if (result.dataUpdatedAt > prevDataUpdatedAt) {
          queriesDataUpdatedAt.set(queryKey, result.dataUpdatedAt);
          startTransition(() => {
            setSteps(result.data as Step[], build);
          });
        }
      });
    });

    return () => {
      unsubscribes.map((unsubscribe) => unsubscribe());
    };
  }, []);

  /**
   * Fallback to referencing steps and jobs directly from the build.
   */
  useEffect(() => {
    if (!Features.ProgressiveBuildShow) {
      setSteps(build.steps, build);
      setJobs(build.jobs);
    }
  }, [build]);

  // Get query for fetching _all_ steps – used for refetching.
  // TODO: Remove once we're refetching only the steps that have changed
  const stepsQuery = useStepsQuery(build, { enabled: false });

  const handleStoreChange = useCallback(() => {
    setBuild(store.build);

    if (Features.ProgressiveBuildShow) {
      // TODO: Remove once we're refetching only the steps that have changed
      stepsQuery.refetch().then((result) => {
        startTransition(() => {
          result.data?.forEach((step) => {
            // Invalidate the step drawer query to ensure we have the latest data
            queryClient.invalidateQueries({
              queryKey: stepQueryKey(build, step.uuid),
              exact: false, // We want to invalidate all queries that match the step
            });
          });
        });
      });
    }
  }, []);

  useEffect(() => {
    store.on("change", handleStoreChange);
    return () => {
      store.off("change", handleStoreChange);
    };
  }, []);

  const loadAndEmit = useCallback(
    (build: Build) => {
      store.loadAndEmit(build);
    },
    [store],
  );

  return (
    <BuildContext.Provider
      value={{
        build: build as Build & Record<string, unknown>,
        setBuild: loadAndEmit,
        store: store,
      }}
    >
      {children}
    </BuildContext.Provider>
  );
};

type StrictBuildContext = {
  build: Build;
  store: BuildShowStore;
  setBuild: (build: Build) => void;
};

type NullableBuildContext = {
  build: (Build & Record<string, unknown>) | null;
  store: BuildShowStore | null;
  setBuild: (build: Build) => void;
};

// Overload signatures
export function useBuild(): StrictBuildContext;
export function useBuild(options: { strict: false }): NullableBuildContext;

// Implementation
export function useBuild(options?: {
  strict?: boolean;
}): StrictBuildContext | NullableBuildContext {
  const context = useContext(BuildContext);

  if (context === undefined) {
    throw new Error("useBuild() must be used within a <BuildContextProvider />");
  }

  if (options?.strict !== false && (!context.build || !context.store)) {
    throw new Error("Build context is missing required build or store");
  }

  return context;
}

/**
 * Fetch the most recent parallel jobs for a given step.
 */
export const useMostRecentParallelJobsForStep = (stepUuid: string): CommandJob[] => {
  const jobs = useJobsForStep<CommandJob>(stepUuid);

  return mostRecentParallelJobs(jobs);
};

/**
 * Fetch the most recent matrix jobs for a given step.
 */
export const useMostRecentMatrixJobsForStep = (stepUuid: string): CommandJob[] => {
  const jobs = useJobsForStep<CommandJob>(stepUuid);

  return mostRecentMatrixJobs(jobs);
};

export function useJobsForStep<T extends Job>(stepUuid: string): T[] {
  const stepJobs = useBuildStore((state) => state.stepJobs);
  const { store } = useBuild({ strict: false });

  if (Features.ProgressiveBuildShow) {
    return (stepJobs.get(stepUuid) || []) as T[];
  }

  if (!store) {
    return [];
  }

  return (store.stepJobs.get(stepUuid) as T[]) || [];
}

export function useMostRecentJob<T extends Job>(stepId: string): T | null {
  const jobs = useJobsForStep<T>(stepId);
  if (!jobs.length) {
    return null;
  }
  return jobs[jobs.length - 1];
}

export function findJobsForStep<T extends Job>(build: Build, stepUuid: string): T[] {
  if (Features.ProgressiveBuildShow) {
    return (useBuildStore.getState().stepJobs.get(stepUuid) || []) as T[];
  }

  return build.jobs.filter((job) => job.stepUuid === stepUuid) as T[];
}

export function mostRecentParallelJobs(jobs: CommandJob[]): CommandJob[] {
  return Array.from(
    jobs
      .reduce((acc, job) => {
        acc.set(job.parallelGroupIndex, job);
        return acc;
      }, new Map())
      .values(),
  );
}

export function mostRecentMatrixJobs(jobs: CommandJob[]): CommandJob[] {
  return Array.from(
    jobs
      .reduce((acc, job) => {
        if (!job.matrix) {
          return acc;
        }

        acc.set(JSON.stringify(job.matrix), job);
        return acc;
      }, new Map())
      .values(),
  );
}

type UseRetriesForJobOptions = { includeLatest?: boolean };

export function useRetriesForJob(
  job: CommandJob,
  { includeLatest = true }: UseRetriesForJobOptions = {},
): CommandJob[] {
  let jobs = useJobsForStep<CommandJob>(job.stepUuid);

  if (job.matrix) {
    jobs = filter(jobs, matchesProperty("matrix", job.matrix));
  }

  if (isNumber(job.parallelGroupIndex)) {
    jobs = filter(jobs, { parallelGroupIndex: job.parallelGroupIndex });
  }

  if (!includeLatest) {
    return dropRight(jobs, 1);
  }

  return jobs;
}

export default BuildContext;
